import React, { FC, Fragment, useContext, useState, useEffect } from 'react';
import { Avatar, IconButton, ListItemIcon, makeStyles, Menu, MenuItem, Theme, Toolbar, Typography } from '@material-ui/core';
import axios, { CancelTokenSource } from 'axios';
import { LOGOUT_URL } from 'constants/url';
import { CurrentUserContext } from 'contexts/CurrentUserContext';
import { getCurrentUserAvatarName, getCurrentUserId, getCurrentUserDisplayName, getCurrentUserLoginName } from 'selectors';
import { CANDIDATE_GLOBAL_SEARCH_URL } from 'constants/url';
import { PRIMARY_COLOR, BACKGROUND_DRAWER_PRIMARY_COLOR } from 'constants/colors';
import clsx from 'clsx';
import LogoutIcon from '@material-ui/icons/PowerSettingsNewRounded';
import IdentifyIcon from '@material-ui/icons/PermIdentityRounded';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import ErrorIcon from '@material-ui/icons/Error';
import logo from 'images/logo.png';
import headerLogo from 'images/header-logo.png';
import UpdateProfileModal from '../../ProfileModal';
import ActionSnackBar from '../../ActionSnackbar';
import useDebounce from 'hooks/useDebounce';

interface Props {
  open?: boolean;
  handleDrawerOpen?(): void;
}

const useStyles = makeStyles((theme: Theme) => ({
  avatar: {
    margin: 10,
    width: 30,
    height: 30,
    backgroundColor: PRIMARY_COLOR,
    border: '2px solid #FFFFFF'
  },
  toolbar: {
    background: BACKGROUND_DRAWER_PRIMARY_COLOR,
    paddingRight: 24 // keep right padding when drawer closed
  },
  menuButton: {
    marginRight: 36
  },
  menuButtonHidden: {
    display: 'none'
  },
  title: {
    flexGrow: 1
  },
  ListItemIcon: {
    minWidth: theme.spacing(5)
  },
  logo: {
    width: '2em'
  },
  headerLogo: {
    height: '1em'
  }
}));

const DashboardHeader: FC<Props> = props => {
  const classes = useStyles();
  const { currentUser, unsetCurrentUser } = useContext(CurrentUserContext);
  
  const currentUserAvatarName = getCurrentUserAvatarName(currentUser);
  const userProfileId = getCurrentUserId(currentUser);
  const userLoginName = getCurrentUserLoginName(currentUser);
  const userDisplayName = getCurrentUserDisplayName(currentUser);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const { open, handleDrawerOpen } = props;
  const [openSnackbar, setOpenSnackbar] = useState<boolean>(false);
  const [snackbarVarient, setSnackbarVarient] = useState<'success' | 'error'>('success');
  const [openProfileModal, setOpenProfileModal] = useState<boolean>(false);
  const [currentEditingUserProfileId, setCurrentEditingUserProfileId] = useState<number>(0);

  const [globalSearchValue, setGlobalSearchValue] = useState<string>('');
  const [openPopper, setOpenPopper] = useState(false);

  const [isSearchingCandidate, setSearchingCandidate] = useState<boolean>(false);
  const [isSearchCandidateError, setSearchCandidateError] = useState<boolean>(false);
  const [candidates, setCandidates] = useState<CandidateSearchModel[]>([]);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleOpenProfileModal = (userProfileId: number): React.MouseEventHandler => () => {
    handleClose();
    setCurrentEditingUserProfileId(userProfileId);
    setOpenProfileModal(true);
  };

  const handleCancelProfileModal = () => {
    setOpenProfileModal(false);
  };

  const handleLogout = async () => {
    handleClose();

    try {
      await axios.post(LOGOUT_URL);
    } catch (err) {
      // do nothing. Log out even if server request failed
    }

    unsetCurrentUser();
  };

  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
  };

  const debouncedSearchTerm = useDebounce(globalSearchValue, 500);

  // Load candiate data to populate on search list
  useEffect(() => {
    const cancelTokenSource: CancelTokenSource = axios.CancelToken.source();
    if (debouncedSearchTerm) {
      if (debouncedSearchTerm.length >= 3) {
        const getQueryParams = () => {
          const params = new URLSearchParams();
          if (debouncedSearchTerm) {
            params.append('q', debouncedSearchTerm);
          }
          return params.toString();
        };

        const searchCandidate = async () => {
          setSearchingCandidate(true);
          setSearchCandidateError(false);

          try {
            const url = `${CANDIDATE_GLOBAL_SEARCH_URL}?${getQueryParams()}`;
            const { data } = await axios.get(url, { cancelToken: cancelTokenSource.token });
            setCandidates(data.candidates);
            setOpenPopper(true);
          } catch (err) {
            setSearchCandidateError(true);
          }

          setSearchingCandidate(false);
        };

        searchCandidate();

        return () => {
          cancelTokenSource.cancel();
        };
      } else {
        setOpenPopper(false);
      }
    } else {
      setOpenPopper(false);
    }
  }, [debouncedSearchTerm]);
  // Only call effect if debounced search term changes
  return (
    <Fragment>
      <Toolbar className={classes.toolbar}>
        <IconButton
          edge='start'
          color='inherit'
          aria-label='Open drawer'
          onClick={handleDrawerOpen}
          className={clsx(classes.menuButton, open && classes.menuButtonHidden)}
        >
          <img src={logo} alt='appLogo' className={classes.logo} />
        </IconButton>
        <Typography component='h1' variant='h5' color='textPrimary' noWrap className={classes.title}>
          <img src={headerLogo} alt='Best Helper' className={classes.headerLogo} />
        </Typography>
        <IconButton size='small' color='inherit' onClick={handleClick}>
          <Avatar className={classes.avatar}>{currentUserAvatarName}</Avatar>
        </IconButton>
        <Menu
          id='profile-menu'
          anchorEl={anchorEl}
          keepMounted
          elevation={1}
          getContentAnchorEl={null}
          open={Boolean(anchorEl)}
          onClose={handleClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'center'
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'center'
          }}
        >
          <MenuItem onClick={handleOpenProfileModal(userProfileId)}>
            <ListItemIcon className={classes.ListItemIcon}>
              <IdentifyIcon />
            </ListItemIcon>
            My Profile Settings
          </MenuItem>
          <MenuItem onClick={handleLogout}>
            <ListItemIcon className={classes.ListItemIcon}>
              <LogoutIcon />
            </ListItemIcon>
            Logout
          </MenuItem>
        </Menu>
      </Toolbar>
      <ActionSnackBar
        variant={snackbarVarient}
        message={snackbarVarient === 'success' ? 'Update is successful' : 'Operation failed'}
        open={openSnackbar}
        handleClose={handleCloseSnackbar}
        Icon={snackbarVarient === 'success' ? CheckCircleIcon : ErrorIcon}
      />
      <UpdateProfileModal
        open={openProfileModal}
        userId={userProfileId}
        userDisplayName={userDisplayName}
        userLoginName={userLoginName}
        handleCancel={handleCancelProfileModal}
        setOpenSnackbar={setOpenSnackbar}
        setSnackbarVarient={setSnackbarVarient}
      />
    </Fragment>
  );
};

export default DashboardHeader;
